import React, { useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { createSharedContext } from './sharedContext';

// Shared across package entrypoints — see sharedContext. This has always been
// exported from both `.` and `/contexts`, so mixing them has always broken;
// anchoring it here fixes that too, and is required for PermissionRequestProvider
// (which calls useNotification) to work when the two come from different
// entrypoints.
const NotificationContext = createSharedContext('Notification');

/**
 * Hook to access notification functions
 * @returns {{ showNotification: (message: string, severity?: 'success' | 'error' | 'warning' | 'info', options?: { action?: { label: string, href?: string, onClick?: () => void } }) => void, hideNotification: () => void }}
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

/**
 * NotificationProvider - Provides global notification/toast functionality
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {number} [props.autoHideDuration=6000] - Duration in ms before auto-hide
 * @param {Object} [props.anchorOrigin={ vertical: 'bottom', horizontal: 'center' }] - Position of snackbar
 *
 * @example
 * // In App.js
 * <NotificationProvider>
 *   <Dashboard />
 * </NotificationProvider>
 *
 * // In any child component
 * const { showNotification } = useNotification();
 * showNotification('Data saved successfully', 'success');
 */
export const NotificationProvider = ({
  children,
  autoHideDuration = 6000,
  anchorOrigin = { vertical: 'bottom', horizontal: 'center' }
}) => {
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info',
    action: null,
  });

  // Third arg ``options.action`` adds a button to the toast:
  //   { label, href }    → opens href in a new tab (e.g. the Permissions app)
  //   { label, onClick } → runs a handler
  // Existing two-arg callers are unaffected (action defaults to none).
  const showNotification = useCallback((message, severity = 'info', options = {}) => {
    setNotification({
      open: true,
      message,
      severity,
      action: options?.action || null,
    });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const { action } = notification;
  // When an action is present we render it in the Alert's ``action`` slot.
  // That slot replaces the Alert's default close (X), so we add our own
  // close button alongside it to preserve manual dismissal. A {href} action
  // renders as a real anchor (new tab) so it works without window.open and
  // is trivially assertable in tests; an {onClick} action runs a handler. The
  // two are not exclusive — a permission-denied action carries both, and its
  // onClick decides at click time whether to preventDefault and handle
  // in-app or let the link through (see getPermissionDeniedAction).
  // With no action, the Alert keeps its built-in close button via onClose.
  const actionNode = action ? (
    <>
      {action.href ? (
        <Button
          color="inherit"
          size="small"
          component="a"
          href={action.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => {
            action.onClick?.(event);
            hideNotification();
          }}
        >
          {action.label}
        </Button>
      ) : (
        <Button
          color="inherit"
          size="small"
          onClick={() => {
            action.onClick?.();
            hideNotification();
          }}
        >
          {action.label}
        </Button>
      )}
      <IconButton
        size="small"
        color="inherit"
        aria-label="close"
        onClick={hideNotification}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  ) : undefined;

  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification }}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={autoHideDuration}
        onClose={hideNotification}
        anchorOrigin={anchorOrigin}
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          variant="filled"
          action={actionNode}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
